import React, { useId, useLayoutEffect, useReducer, useRef } from 'react';
import { MenuItem, TextField } from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import {
  CountryCode,
  getCountries,
  getCountryCallingCode,
  PhoneNumber,
  validatePhoneNumberLength,
} from 'libphonenumber-js';

import useHomeStyles from 'components/CallScribe/Home/homeStyles';
import { describeNational, readPastedNumber } from 'components/CallScribe/Home/dialGuards';
import {
  caretIndexForDigits,
  countDigitsBefore,
  deleteAcrossSeparator,
  formatNational,
} from 'components/CallScribe/Home/dialFormat';
import Field, { fieldHelpId, FieldInput, TFieldHelperTone } from 'components/CallScribe/ui/Field';

export interface IDialCombobox {
  listId: string;
  expanded: boolean;
  activeId?: string;
}

export interface ICountryDialFieldProps {
  country: CountryCode;
  national: string;
  invalid: boolean;
  inputRef?: React.Ref<HTMLInputElement>;
  inputId?: string;
  label?: string;
  inputLabel?: string;
  placeholder?: string;
  helper?: string;
  helperTone?: TFieldHelperTone;
  query?: string;
  onQueryChange?: (query: string) => void;
  onCountryChange: (country: CountryCode) => void;
  onNationalChange: (raw: string) => void;
  /** Enter in the number field places the call, the same as clicking the button beside it. */
  onSubmit?: () => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => boolean;
  onFocus?: () => void;
  onBlur?: () => void;
  combobox?: IDialCombobox;
  countryMenuOpen?: boolean;
  onCountryMenuOpenChange?: (open: boolean) => void;
  trailing?: React.ReactNode;
  helperAction?: React.ReactNode;
  hideLabel?: boolean;
}

const flagEmoji = (code: string): string =>
  String.fromCodePoint(...[...code.toUpperCase()].map(c => 0x1f1e6 + c.charCodeAt(0) - 65));

const regionNames = new Intl.DisplayNames(['en'], { type: 'region' });

const COUNTRY_OPTIONS = getCountries()
  .map(code => ({
    code,
    name: regionNames.of(code) ?? code,
    dial: getCountryCallingCode(code),
  }))
  .sort((a, b) => a.name.localeCompare(b.name));

const LETTER = /\p{L}/u;

export const numberHelper = (
  national: string,
  country: CountryCode,
  parsed: PhoneNumber | undefined,
): { text: string; tone: TFieldHelperTone } => {
  if (parsed?.isValid()) {
    const place = parsed.country ? regionNames.of(parsed.country) : undefined;
    return { text: place ? `Valid number · ${place}` : 'Valid number', tone: 'ok' };
  }
  if (validatePhoneNumberLength(national, country) === 'TOO_SHORT') {
    return { text: `Keep typing: that is too short for +${getCountryCallingCode(country)}.`, tone: 'info' };
  }
  return { text: describeNational(national, country), tone: 'bad' };
};

const CountryDialField: React.FC<ICountryDialFieldProps> = ({
  country,
  national,
  invalid,
  inputRef,
  inputId,
  label = 'Phone number',
  inputLabel,
  placeholder = 'Phone number',
  helper,
  helperTone,
  query = '',
  onQueryChange,
  onSubmit,
  onCountryChange,
  onNationalChange,
  onKeyDown,
  onFocus,
  onBlur,
  combobox,
  countryMenuOpen,
  onCountryMenuOpenChange,
  trailing,
  helperAction,
  hideLabel = false,
}) => {
  const classes = useHomeStyles();
  const generatedId = useId();
  const id = inputId ?? generatedId;
  const allowNames = Boolean(onQueryChange);
  const searching = allowNames && query.length > 0;
  const problem = searching ? '' : describeNational(national, country);

  const formatted = formatNational(country, national);
  const shown = searching ? query : formatted;
  const element = useRef<HTMLInputElement | null>(null);
  const pendingDigits = useRef<number | null>(null);
  const [, rerender] = useReducer((n: number) => n + 1, 0);

  useLayoutEffect(() => {
    const el = element.current;
    if (pendingDigits.current === null || !el) return;
    const at = caretIndexForDigits(formatted, pendingDigits.current);
    pendingDigits.current = null;
    el.setSelectionRange(at, at);
  });

  const setInputRef = (node: HTMLInputElement | null): void => {
    element.current = node;
    if (typeof inputRef === 'function') inputRef(node);
    else if (inputRef) (inputRef as React.MutableRefObject<HTMLInputElement | null>).current = node;
  };

  const edit = (raw: string, caretDigits: number): void => {
    if (searching) onQueryChange?.('');
    pendingDigits.current = caretDigits;
    onNationalChange(raw);
    rerender();
  };

  const search = (text: string): void => {
    if (national) onNationalChange('');
    onQueryChange?.(text);
  };

  const clear = (): void => {
    if (query) onQueryChange?.('');
    if (national) onNationalChange('');
    element.current?.focus();
  };

  const selectProps = {
    renderValue: (v: unknown) => `${flagEmoji(String(v))} +${getCountryCallingCode(v as CountryCode)}`,
    MenuProps: { PaperProps: { style: { maxHeight: 320 } } },
    ...(countryMenuOpen === undefined
      ? {}
      : {
          open: countryMenuOpen,
          onOpen: () => { onCountryMenuOpenChange?.(true); },
          onClose: () => { onCountryMenuOpenChange?.(false); },
        }),
  };

  return (
    <Field
      label={label}
      inputId={id}
      helper={helper ?? problem}
      helperTone={helperTone ?? (problem ? 'bad' : 'info')}
      invalid={!searching && (invalid || problem !== '')}
      helperAction={helperAction}
      hideLabel={hideLabel}
    >
      <TextField
        select
        size="small"
        className={classes.countrySelect}
        value={country}
        onChange={e => { onCountryChange(e.target.value as CountryCode); }}
        SelectProps={selectProps}
        inputProps={{ 'aria-label': 'Country' }}
      >
        {COUNTRY_OPTIONS.map(o => (
          <MenuItem key={o.code} value={o.code}>
            <span className={classes.countryOption}>
              <span aria-hidden>{flagEmoji(o.code)}</span>
              <span className={classes.countryOptionName}>{o.name}</span>
              <span className={classes.countryOptionDial}>+{o.dial}</span>
            </span>
          </MenuItem>
        ))}
      </TextField>
      <FieldInput
        id={id}
        ref={setInputRef}
        value={shown}
        placeholder={placeholder}
        aria-label={inputLabel}
        aria-invalid={!searching && (invalid || problem !== '')}
        aria-describedby={fieldHelpId(id)}
        inputMode={allowNames ? 'text' : 'tel'}
        autoComplete={allowNames ? 'off' : 'tel'}
        spellCheck={false}
        role={combobox ? 'combobox' : undefined}
        aria-autocomplete={combobox ? 'list' : undefined}
        aria-expanded={combobox ? combobox.expanded : undefined}
        aria-controls={combobox ? combobox.listId : undefined}
        aria-activedescendant={combobox?.expanded ? combobox.activeId : undefined}
        onFocus={onFocus}
        onBlur={onBlur}
        onChange={e => {
          const { value, selectionStart } = e.target;
          if (allowNames && LETTER.test(value)) {
            search(value);
            return;
          }
          edit(value, countDigitsBefore(value, selectionStart ?? value.length));
        }}
        onPaste={e => {
          const text = e.clipboardData.getData('text');
          if (!/[+a-z]/i.test(text)) return;
          e.preventDefault();
          if (allowNames && LETTER.test(text) && !readPastedNumber(text, country)) {
            search(text.trim());
            return;
          }
          edit(text, Number.MAX_SAFE_INTEGER);
        }}
        onKeyDown={e => {
          if (onKeyDown?.(e)) return;
          if ((e.key === 'Backspace' || e.key === 'Delete') && !searching) {
            const el = element.current;
            if (!el) return;
            const change = deleteAcrossSeparator(
              el.value,
              el.selectionStart ?? 0,
              e.key,
              el.selectionEnd ?? el.selectionStart ?? 0,
            );
            if (change) {
              e.preventDefault();
              edit(change.digits, change.caretDigits);
            }
            return;
          }
          if (e.key !== 'Enter') return;
          // The button is a `type="button"` sibling rather than a submit inside a form, so Enter here
          // cannot also activate it — no double dial. The guard lives in the handler itself.
          e.preventDefault();
          onSubmit?.();
        }}
      />
      {allowNames && shown ? (
        <button type="button" className={classes.fieldButton} aria-label="Clear" onClick={clear}>
          <CloseRoundedIcon aria-hidden style={{ fontSize: 24 }} />
        </button>
      ) : null}
      {trailing}
    </Field>
  );
};

export default CountryDialField;
